import {Alert, Box, Stack, TextField, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import React from "react";
import {useTranslation} from "react-i18next";
import {DEFAULT_PRINTOUT_FONT_FAMILY, getPrintoutFontFamily} from "../core/printoutFonts";
import {printoutTheme, printoutTypeSx} from "../core/printoutTheme";
import {usePrintoutFontStylesheet} from "../core/usePrintoutFontStylesheet";

export interface PrintoutFontValue {
    fontFamily?: string;
    headingFontFamily?: string;
    fontUrl?: string;
}

interface Props {
    hasLayouts: boolean;
    onChange: (value: PrintoutFontValue) => void;
    value: PrintoutFontValue;
}

const extractGoogleFontFamilies = (value: string): string[] => {
    try {
        const url = new URL(value);
        if (url.hostname !== "fonts.googleapis.com") return [];

        return url.searchParams.getAll("family")
            .flatMap(family => family.split("|"))
            .map(family => family.split(":")[0].trim())
            .filter(Boolean)
            .map(family => `"${family}", ${DEFAULT_PRINTOUT_FONT_FAMILY}`);
    } catch {
        return [];
    }
};

const PrintoutFontFields = ({hasLayouts, onChange, value}: Props) => {
    const {t} = useTranslation();
    usePrintoutFontStylesheet(value.fontUrl);

    const bodyFont = getPrintoutFontFamily(value.fontFamily);
    const headingFont = value.headingFontFamily?.trim() || bodyFont;

    return (
        <Stack gap={{xs: 1.5, sm: 1}}>
            <Alert severity="info" sx={{alignItems: "center", py: 0}} variant="outlined">
                {t("One stylesheet can load both fonts. Google Fonts families are filled in automatically; you can edit them below.")}
            </Alert>

            <Property title={t("Font URL")} tooltip={t("Public stylesheet URL, usually from Google Fonts.")}>
                <TextField
                    fullWidth
                    inputProps={{"aria-label": t("Font URL")}}
                    onChange={(event) => {
                        const fontUrl = event.target.value;
                        const [body, heading] = extractGoogleFontFamilies(fontUrl);
                        onChange({
                            ...value,
                            fontUrl,
                            ...(body ? {fontFamily: body, headingFontFamily: heading} : {}),
                        });
                    }}
                    size={"small"}
                    value={value.fontUrl ?? ""}
                />
            </Property>

            <Property title={t("Body font")} tooltip={t("Font family for paragraphs, labels and large values.")}>
                <TextField
                    fullWidth
                    inputProps={{"aria-label": t("Body font")}}
                    onChange={(event) => onChange({...value, fontFamily: event.target.value})}
                    placeholder={DEFAULT_PRINTOUT_FONT_FAMILY}
                    size="small"
                    value={value.fontFamily ?? ""}
                />
            </Property>

            <Property title={t("Heading font")} tooltip={t("Font family for headings and display titles in every language. Leave empty to use the body font.")}>
                <TextField
                    disabled={!hasLayouts}
                    fullWidth
                    helperText={!hasLayouts ? t("Add a language before choosing a heading font.") : undefined}
                    inputProps={{"aria-label": t("Heading font")}}
                    onChange={(event) => onChange({...value, headingFontFamily: event.target.value})}
                    placeholder={bodyFont}
                    size="small"
                    value={value.headingFontFamily ?? ""}
                />
            </Property>

            <Property labelAlignment={"top"} title={t("Preview")}>
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 2,
                        width: "100%",
                    }}
                >
                    <Typography sx={{...printoutTypeSx(printoutTheme.type.displayTitle), fontFamily: headingFont, mb: "4mm"}}>
                        {t("A moment for you")}
                    </Typography>
                    <Typography sx={{...printoutTypeSx(printoutTheme.type.body1), fontFamily: bodyFont}}>
                        {t("The quick brown fox jumps over the lazy dog. 0123456789 !?&%$")}
                    </Typography>
                </Box>
            </Property>
        </Stack>
    );
};

export default PrintoutFontFields;
